<template>
  <p>
    Personaliza tus estrategias de correos masivos creando Parámetros. Completa datos específicos de 
    cada contacto para hacer tus correos más amigables. Recuerda que para utilizar tu parámetro, 
    deberás escribir el <strong>símbolo “#”</strong> en el texto de tu correo.
  </p>

  <a-form class="form" ref="formRef" layout="vertical" :model="formState" @finish="handleSubmit">
    <a-form-item name="name" :rules="nameRules" label="Nombre del parámetro" :colon="false">
      <a-input class="input" v-model:value="formState.name" placeholder="Escribir aquí..." />
    </a-form-item>

    <a-button type="primary" html-type="submit" style="margin-top: 23px">Crear parámetro</a-button>
  </a-form>

  <p class="info">
    El nombre debe ser en minúsculas, separados por un guion bajo, sin espacios y sin caracteres
    especiales. Ejemplo: producto_123
  </p>

  <a-table
    class="table"
    size="small"
    :columns="columns"
    :data-source="variables"
    :pagination="false"
    :scroll="scrollStyle"
  >
    <template #bodyCell="{ column, index, record }">
      <template v-if="column.key === 'actions'">
        <div class="actions">
          <a-button class="button" type="link" @click="handleEdit(record, index)">
            <EditOutlined />
          </a-button>

          <a-button class="button" type="link" @click="handleDelete(index)">
            <DeleteOutlined />
          </a-button>
        </div>
      </template>
    </template>
  </a-table>

  <EditVariableModal
    v-if="openEditModal && variableToEdit"
    v-model:open="openEditModal"
    :variable="variableToEdit"
    :template="template"
    @update="emit('update')"
  />
</template>

<script setup lang="ts">
import { DeleteOutlined, EditOutlined, QuestionCircleOutlined } from "@ant-design/icons-vue";
import { ColumnsType } from "ant-design-vue/es/table";
import { FormInstance, Modal } from "ant-design-vue";
import { reactive, ref, onMounted, createVNode, watch } from "vue";

import { Template, Variable } from "@/@types/flows/emailTemplates";
import { variablesService } from "@/app/flows/emailTemplates/services/Variables.service";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import { variableNameRegex } from "@/app/flows/emailTemplates/utils/regex";

import EditVariableModal from "@/app/flows/emailTemplates/components/modals/EditVariableModal.vue";

const columns: ColumnsType = [
  {
    title: "Nª",
    customRender: ({ index }) => index + 1,
    width: 80,
  },
  {
    title: "Nombre del parámetro",
    dataIndex: "name",
  },
  {
    title: "Acciones",
    key: "actions",
    width: 120,
  },
];

const nameRules = [
  { required: true, message: "El nombre es requerido" },
  {
    pattern: variableNameRegex,
    message: "El nombre no cumple con los parámetros",
  },
];

const props = defineProps<{ template: Template | undefined }>();
const emit = defineEmits<{ update: [] }>();

const formRef = ref<FormInstance>();
const formState: { name: string } = reactive({ name: "" });
const { scrollStyle } = useScrollResize({ headerMargin: 460, width: 0 });

const variables = ref<Variable[]>([]);

const fetchVariables = async () => {
  variables.value = props.template?.parameters || [];
};

watch(
  () => props.template?.parameters,
  () => {
    fetchVariables();
  }
);

onMounted(() => fetchVariables());

const handleSubmit = async (variable: { name: string }) => {
  if (!props.template) return;

  await variablesService.create(props.template, variable);
  formRef.value?.resetFields();
  emit("update");
};

const handleDelete = async (idx: number) => {
  Modal.confirm({
    centered: true,
    icon: createVNode(QuestionCircleOutlined),
    title: "¿Estás seguro de eliminar esta variable?",
    content:
      "Recuerda que si la encuentras utilizando en tu plantilla, también será eliminada. No olvides reemplazarla.",
    okText: "Eliminar",
    onOk: async () => {
      if (!props.template) return;

      await variablesService.delete(props.template, idx);

      emit("update");
    },
  });
};

const openEditModal = ref(false);
const variableToEdit = ref<Variable & { idx: number }>();

const handleEdit = async (variable: Variable, idx: number) => {
  openEditModal.value = true;
  variableToEdit.value = { ...variable, idx };
};
</script>

<style scoped lang="sass">
p
  color: $text-primary
  margin-bottom: 24px

  &.info
    margin: 0
    margin-top: 2px
    color: $text-tertiary
    text-wrap: pretty
    width: calc(100% - 150px)

.form
  display: flex
  align-items: flex-start
  gap: 24px
  width: 100%

  .ant-form-item:first-child
    flex: 1 1 auto

  .ant-form-item
    margin: 0

.table
  margin-top: 24px

  .actions
    display: flex
    gap: 16px

  .button
    padding: 0
    font-size: 16px
</style>
